/**
 * ActivityCard - Display a single running activity
 * Follows ReportCard.tsx pattern
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/utils/cn';
import { useRunningStore } from '@/stores/useRunningStore';
import { renderRouteArt } from '@/utils/routeArt';
import type { RunningActivity } from '@/types/running';

interface ActivityCardProps {
  activity: RunningActivity;
  onUserClick?: (userId: string) => void;
  onActivityClick?: (activityId: string) => void;
}

export function ActivityCard({ activity, onUserClick, onActivityClick }: ActivityCardProps) {
  const { giveKudos, removeKudos, deleteActivity, activityFeed } = useRunningStore();
  const [isKudos, setIsKudos] = useState(activity.is_kudos || false);
  const [kudosCount, setKudosCount] = useState(activity.kudos_count);
  const [isGivingKudos, setIsGivingKudos] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(activity.title);
  const [editDescription, setEditDescription] = useState(activity.description || '');

  // Check if this is the user's own post (local posts have user_id = 'current-user')
  const isOwnPost = activity.user_id === 'current-user';

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
    setShowMenu(false);
  };

  const confirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteActivity(activity.id);
    setShowDeleteConfirm(false);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setShowMenu(false);
  };

  const saveEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Update the activity in the store
    const updatedFeed = activityFeed.map(a =>
      a.id === activity.id
        ? { ...a, title: editTitle, description: editDescription }
        : a
    );
    useRunningStore.setState({ activityFeed: updatedFeed });
    setIsEditing(false);
  };

  const cancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTitle(activity.title);
    setEditDescription(activity.description || '');
    setIsEditing(false);
  };

  const handleKudos = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isGivingKudos) return;

    setIsGivingKudos(true);
    if (isKudos) {
      setIsKudos(false);
      setKudosCount((c) => c - 1);
      await removeKudos(activity.id);
    } else {
      setIsKudos(true);
      setKudosCount((c) => c + 1);
      await giveKudos(activity.id);
    }
    setIsGivingKudos(false);
  };

  const formatDistance = (meters: number): string => {
    return (meters / 1000).toFixed(2);
  };

  const formatPace = (secondsPerKm: number): string => {
    if (!secondsPerKm || secondsPerKm === 0) return '--:--';
    const mins = Math.floor(secondsPerKm / 60);
    const secs = Math.floor(secondsPerKm % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m ${secs}s`;
  };

  const getAQIColor = (aqi: number | undefined): string => {
    if (!aqi) return 'text-gray-400';
    if (aqi <= 50) return 'text-emerald-400';
    if (aqi <= 100) return 'text-yellow-400';
    if (aqi <= 150) return 'text-orange-400';
    return 'text-red-400';
  };

  const getAQIBgColor = (aqi: number | undefined): string => {
    if (!aqi) return 'bg-gray-500/20';
    if (aqi <= 50) return 'bg-emerald-500/20';
    if (aqi <= 100) return 'bg-yellow-500/20';
    if (aqi <= 150) return 'bg-orange-500/20';
    return 'bg-red-500/20';
  };

  const activityTypeEmoji: Record<string, string> = {
    run: '🏃',
    walk: '🚶',
    hike: '🥾',
    trail_run: '🌲',
    treadmill: '🏋️',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'bg-glass-bg backdrop-blur-xl border border-glass-border rounded-2xl overflow-hidden',
        'hover:border-emerald-500/30 transition-colors cursor-pointer'
      )}
      onClick={() => onActivityClick?.(activity.id)}
    >
      {/* Header - User info */}
      <div className="p-4 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="font-semibold text-white truncate hover:underline cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onUserClick?.(activity.user_id);
              }}
            >
              {activity.user?.first_name || activity.user?.username || 'Runner'}
            </span>
            <span className="text-lg">{activityTypeEmoji[activity.activity_type] || '🏃'}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span>{formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}</span>
            {activity.start_location?.name && (
              <>
                <span>•</span>
                <span className="truncate">{activity.start_location.name}</span>
              </>
            )}
          </div>
        </div>

        {/* AQI Badge - Blurp.ie unique */}
        {activity.aqi_data && (
          <div className={cn(
            'px-3 py-1.5 rounded-xl',
            getAQIBgColor(activity.aqi_data.average_aqi)
          )}>
            <div className={cn('text-sm font-bold', getAQIColor(activity.aqi_data.average_aqi))}>
              AQI {activity.aqi_data.average_aqi}
            </div>
            <div className="text-[10px] text-text-secondary">
              {activity.aqi_data.breathed_air_quality}
            </div>
          </div>
        )}

        {/* Menu button for own posts */}
        {isOwnPost && (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-2 text-text-secondary hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>

            {/* Dropdown menu */}
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-32 bg-slate-800 border border-glass-border rounded-xl overflow-hidden shadow-xl z-10">
                <button
                  onClick={handleEdit}
                  className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-slate-700 flex items-center gap-2"
                >
                  <span>✏️</span> Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-slate-700 flex items-center gap-2"
                >
                  <span>🗑️</span> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteConfirm(false);
          }}
        >
          <div
            className="bg-slate-800 border border-glass-border rounded-2xl p-6 max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-2">Delete Activity?</h3>
            <p className="text-text-secondary text-sm mb-4">
              This action cannot be undone. Your activity will be permanently deleted.
            </p>
            <div className="flex gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(false);
                }}
                className="flex-1 py-2.5 rounded-xl bg-slate-700 text-white font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity Title */}
      <div className="px-4 pb-2">
        {isEditing ? (
          <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-glass-border text-white focus:border-emerald-500/50 focus:outline-none"
              placeholder="Activity title"
            />
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-glass-border text-white focus:border-emerald-500/50 focus:outline-none resize-none"
              placeholder="Description (optional)"
              rows={2}
            />
            <div className="flex gap-2">
              <button
                onClick={cancelEdit}
                className="flex-1 py-2 rounded-lg bg-slate-600 text-white text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                className="flex-1 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="text-white font-medium">{activity.title}</h3>
            {activity.description && (
              <p className="text-text-secondary text-sm mt-1">{activity.description}</p>
            )}
          </>
        )}
      </div>

      {/* Stats Grid */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-4 gap-2 p-3 bg-slate-800/50 rounded-xl">
          <div className="text-center">
            <div className="text-xl font-bold text-white">{formatDistance(activity.distance_meters)}</div>
            <div className="text-[10px] text-text-secondary uppercase">km</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-white">{formatPace(activity.pace_seconds_per_km)}</div>
            <div className="text-[10px] text-text-secondary uppercase">/km</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-white">{formatDuration(activity.duration_seconds)}</div>
            <div className="text-[10px] text-text-secondary uppercase">time</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-white">{activity.elevation_gain_meters || 0}</div>
            <div className="text-[10px] text-text-secondary uppercase">m elev</div>
          </div>
        </div>
      </div>

      {/* Route Art Preview (AQI-colored) */}
      {activity.route_polyline && activity.route_polyline.length >= 2 && (
        <RouteArtPreview route={activity.route_polyline} />
      )}

      {/* Scent Reports During Run */}
      {activity.scent_reports && activity.scent_reports.length > 0 && (
        <div className="mx-4 mb-4 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <span>☁️</span>
            <span className="text-xs text-cyan-400 font-medium">Scents Reported During Run</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {activity.scent_reports.map((report, i) => (
              <span key={i} className="text-xs bg-slate-700/50 px-2 py-1 rounded-full text-white">
                {report.scent_notes} @ {report.km_mark.toFixed(1)}km
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 pb-4 flex items-center gap-4">
        {/* Kudos */}
        <button
          onClick={handleKudos}
          className={cn(
            'flex items-center gap-1.5 transition-colors',
            isKudos ? 'text-orange-400' : 'text-text-secondary hover:text-orange-400'
          )}
        >
          <motion.span
            animate={isKudos ? { scale: [1, 1.3, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            {isKudos ? '👏' : '🤝'}
          </motion.span>
          <span className="text-sm">{kudosCount} kudos</span>
        </button>

        {/* Comment */}
        <button className="flex items-center gap-1.5 text-text-secondary hover:text-white transition-colors">
          <span>💬</span>
          <span className="text-sm">{activity.comments_count}</span>
        </button>

        {/* Share */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onActivityClick?.(activity.id);
          }}
          className="flex items-center gap-1.5 text-text-secondary hover:text-emerald-400 transition-colors"
        >
          <span>📤</span>
          <span className="text-sm">Share</span>
        </button>

        <div className="flex-1" />

        {/* Activity type badge */}
        <span className="text-xs bg-slate-700/50 px-2 py-1 rounded-full text-text-secondary capitalize">
          {activity.activity_type.replace('_', ' ')}
        </span>
      </div>
    </motion.div>
  );
}

/** Renders route art from saved route_polyline data */
function RouteArtPreview({ route }: { route: RunningActivity['route_polyline'] }) {
  const [src, setSrc] = useState<string | null>(null);
  const rendered = useRef(false);

  useEffect(() => {
    if (rendered.current || !route || route.length < 2) return;
    rendered.current = true;
    try {
      const canvas = renderRouteArt(route, 400, 160, {
        backgroundColor: '#0f172a',
        padding: 20,
        lineWidth: 2,
        glowWidth: 6,
      });
      setSrc(canvas.toDataURL('image/png'));
    } catch {
      // Silently fail
    }
  }, [route]);

  if (!src) return null;

  return (
    <div className="mx-4 mb-4 rounded-xl overflow-hidden">
      <img src={src} alt="Route" className="w-full h-32 object-cover" />
    </div>
  );
}

export default ActivityCard;
